import React from "react";
import useInput from "@ui/lib/hooks/useInput";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { usePost } from "../../../api/useCall";
import InputV2 from "@ui/Input/InputV2";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import Select, { type SelectOption } from "@ui/Select";
import Text from "@ui/Text";
import { assertExists } from "diagnostics-lib/typescript";
import { getGlobalRoleOptionsBase } from "./globalRoleOptions";
import type { GlobalRole } from "../../../api/useUser";
import { useCanUserDo } from "../../canUserDo";

interface P {
  onRequestClose: () => void;
}

const CreateUserModalForm = ({ onRequestClose }: P) => {
  const createUser = usePost("/users");
  const [submitted, setSubmitted] = React.useState(false);
  const inputName = useInput();
  const { addToast } = useToasts();
  const canAssignGlobalRole = useCanUserDo("ASSIGN_GLOBAL_ROLE");

  const baseOptions: SelectOption[] = React.useMemo(() => getGlobalRoleOptionsBase(), []);
  const options = React.useMemo(
    () => (canAssignGlobalRole ? baseOptions : []),
    [baseOptions, canAssignGlobalRole],
  );
  const [selectOption, setSelectOption] = React.useState<SelectOption | null>(
    canAssignGlobalRole ? assertExists(baseOptions[0]) : null,
  );

  const errorInputUser = React.useMemo((): string | undefined => {
    if (inputName.value === "") return t("users.modal.create.input.name.error.cannot_be_empty");
    if (isLeft(short(io.string).decode(inputName.value)))
      return t("users.modal.create.input.login.error.text_to_long");
    return undefined;
  }, [inputName.value]);

  const valid = errorInputUser === undefined;
  const dirty = submitted || inputName.dirty;

  const handleSubmit = React.useCallback(async () => {
    setSubmitted(true);
    if (!valid) return false;
    const globalRole = canAssignGlobalRole ? ((selectOption?.value as GlobalRole) ?? null) : null;
    await createUser({
      body: {
        login: inputName.value,
        globalRole,
      },
    });
    addToast("success", t("users.modal.create.create_user.toast", { name: inputName.value }));
    return true;
  }, [addToast, canAssignGlobalRole, createUser, inputName.value, valid, selectOption]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={dirty && !errorInputUser ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.add")}
    >
      <Text>{t("users.modal.create.create_user.text")}</Text>
      <InputV2
        label={t("users.modal.create.create_user.subtitle")}
        required
        autoFocus
        error={inputName.pristine ? undefined : errorInputUser}
        {...inputName}
      />
      <Select
        prompt={t("common.select.empty")}
        label={t("users.modal.create.select.global_role.label")}
        options={options}
        value={selectOption?.value ?? ""}
        disabled={!canAssignGlobalRole}
        onChange={e => {
          if (!e.target.value) {
            setSelectOption(null);
            return;
          }
          const selected = options.find(option => option.value === e.target.value) || null;
          setSelectOption(selected);
        }}
        revertable={false}
      />
    </ModalForm>
  );
};

export default CreateUserModalForm;
